import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Home, Calendar, Clock, User, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrokerRequests = () => {
  const navigate = useNavigate();
  const { token, agent } = useAuth();
  const [requests, setRequests] = useState([]);
  const [agents, setAgents] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    property_address: '',
    date: '',
    time: '13:00',
    end_time: '16:00',
    description: '',
    assigned_agent_id: ''
  });

  useEffect(() => {
    if (agent?.role === 'broker') {
      fetchData();
    }
  }, [agent]);

  const fetchData = async () => {
    try {
      const [requestsRes, agentsRes] = await Promise.all([
        axios.get(`${API}/broker/open-house-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/broker/agents`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setRequests(requestsRes.data);
      setAgents(agentsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/broker/open-house-requests`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Open house request created!');
      setShowCreateModal(false);
      setFormData({
        property_address: '',
        date: '',
        time: '13:00',
        end_time: '16:00',
        description: '',
        assigned_agent_id: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create request');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Unassigned', color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
      assigned: { label: 'Assigned', color: 'bg-blue-100 text-blue-700', icon: User },
      completed: { label: 'Event Created', color: 'bg-green-100 text-green-700', icon: CheckCircle }
    };
    return statusConfig[status] || statusConfig.pending;
  };

  if (agent?.role !== 'broker') {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto text-center py-12">
          <p className="text-lg text-stone-600">This page is only accessible to brokers</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/broker')}
            className="mb-4 text-stone-600 hover:text-[#1A4D2E]"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Dashboard
          </Button>
        </div>
        
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>
              Open House Requests
            </h1>
            <p className="text-stone-600">Create and assign open houses to your agents</p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-[#1A4D2E] text-white rounded-full px-8 py-6 text-lg hover:bg-[#143D24]"
            data-testid="create-request-button"
          >
            <Plus className="mr-2" size={20} />
            Create Request
          </Button>
        </div>

        {loading ? (
          <p className="text-stone-500">Loading...</p>
        ) : requests.length === 0 ? (
          <Card className="bg-white p-12 rounded-2xl card-shadow text-center">
            <Home className="mx-auto mb-4 text-stone-300" size={48} />
            <p className="text-lg text-stone-600 mb-4">No open house requests yet</p>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-[#1A4D2E] text-white rounded-full px-8 py-6"
            >
              <Plus className="mr-2" size={18} />
              Create First Request
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {requests.map((request) => {
              const statusBadge = getStatusBadge(request.status);
              const StatusIcon = statusBadge.icon;
              
              return (
                <Card
                  key={request.id}
                  className="bg-white p-6 rounded-2xl card-shadow hover:card-shadow-hover hover:-translate-y-1 transition-all duration-300"
                  data-testid={`request-card-${request.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 rounded-full bg-[#1A4D2E]/10">
                      <Home className="text-[#1A4D2E]" size={24} />
                    </div>
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${statusBadge.color}`}>
                      <StatusIcon size={14} />
                      {statusBadge.label}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-[#1A4D2E] mb-3 line-clamp-2" style={{fontFamily: '"Playfair Display", serif'}}>
                    {request.property_address}
                  </h3>

                  <div className="space-y-2 text-sm text-stone-600 mb-4">
                    <div className="flex items-center gap-2">
                      <Calendar size={16} />
                      <span>{new Date(request.date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock size={16} />
                      <span>{request.time}</span>
                    </div>
                    {request.assigned_agent_name && (
                      <div className="flex items-center gap-2">
                        <User size={16} />
                        <span>{request.assigned_agent_name}</span>
                      </div>
                    )}
                  </div>

                  {request.event_id && (
                    <Button
                      onClick={() => navigate(`/broker/events/${request.event_id}`)}
                      className="w-full bg-[#1A4D2E] text-white rounded-full hover:bg-[#143D24]"
                    >
                      View Event
                    </Button>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Create Request Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-[90vw] sm:max-w-md max-h-[85vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader className="pb-2">
              <DialogTitle className="text-lg font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>
                Create Open House Request
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-2.5">
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1 block">Property Address</Label>
                <Input
                  data-testid="request-address"
                  type="text"
                  name="property_address"
                  value={formData.property_address}
                  onChange={handleChange}
                  className="h-9 bg-stone-50 border border-stone-200 focus:border-[#1A4D2E] rounded-lg px-2.5 text-xs"
                  required
                />
              </div>

              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1 block">Date</Label>
                <Input
                  data-testid="request-date"
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="h-9 bg-stone-50 border border-stone-200 focus:border-[#1A4D2E] rounded-lg px-2.5 text-xs"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1 block">Start Time</Label>
                  <Input
                    data-testid="request-time"
                    type="time"
                    name="time"
                    value={formData.time}
                    onChange={handleChange}
                    className="h-9 bg-stone-50 border border-stone-200 focus:border-[#1A4D2E] rounded-lg px-2 text-xs"
                    required
                  />
                </div>
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1 block">End Time</Label>
                  <Input
                    data-testid="request-end-time"
                    type="time"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleChange}
                    className="h-9 bg-stone-50 border border-stone-200 focus:border-[#1A4D2E] rounded-lg px-2 text-xs"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1 block">Description</Label>
                <Textarea
                  data-testid="request-description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="min-h-[60px] bg-stone-50 border border-stone-200 focus:border-[#1A4D2E] rounded-lg px-2.5 py-2 text-xs resize-none"
                />
              </div>

              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1 block">Assign to Agent (Optional)</Label>
                <select
                  data-testid="request-agent"
                  name="assigned_agent_id"
                  value={formData.assigned_agent_id}
                  onChange={handleChange}
                  className="h-9 w-full bg-stone-50 border border-stone-200 focus:border-[#1A4D2E] rounded-lg px-2.5 text-xs"
                >
                  <option value="">Unassigned</option>
                  {agents.map((ag) => (
                    <option key={ag.id} value={ag.id}>{ag.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 border border-stone-300 rounded-full h-9 text-xs"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="submit-request"
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-[#1A4D2E] text-white rounded-full h-9 hover:bg-[#143D24] text-xs"
                >
                  {loading ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default BrokerRequests;
